import { ReactNode } from "react";
import { Toolbar, ToolbarItem } from "@/components/ui/toolbar";
import { Button } from "@/components/ui/button";
import { CustomDropdown, CustomDropdownItem } from "@/components/ui/dropdown";
import { ArrowUpOnSquareIcon } from "@heroicons/react/24/outline";
import { ComputerDesktopIcon, PhotoIcon, TableCellsIcon } from "@heroicons/react/20/solid";
import { useCreateImages, useCreatePowerPoint } from "@/api/studio/exports/queries";
import { useModalStore } from "@/stores/use-modal-store";
import { useSegment } from "@/hooks/use-segment";
import { Select, SelectItem } from "@/components/ui/select";
import { useReportFiltersStore } from "../stores/use-report-filters-store";
import { useReportsSceneStore, SceneMetric } from "../stores/use-reports-scene-store";
import { PencilSquareIcon } from "@heroicons/react/20/solid";
import { ExportPowerPointModal } from "../modals/export-powerpoint-modal";
import { ExportVisualsModal } from "../modals/export-visuals-modal";
import { ExportDataModal } from "../modals/export-data-modal";
import { EditStudyModal } from "../modals/edit-study-modal";
import { InfoModalTrigger } from "@/components/ui/modal";
import { getSceneInfoModal } from "../utils/get-scene-info-modal";
import { useFeatureFlag } from "@/hooks/use-feature-flag";

/* Prop Types */
interface ReportsSceneToolbarProps {
  projectId: string;
  studyId: string;
  rightContent?: ReactNode;
  showFilters?: boolean;
}

/*
 * ReportsSceneToolbar Component
 * Displays toolbar for scene reports with metric filters (Visibility, POP, SOA, POW)
 */
export const ReportsSceneToolbar = ({ projectId, studyId, rightContent, showFilters = true }: ReportsSceneToolbarProps) => {
  /* Queries */
  const { mutateAsync: createPowerPoint, isPending: isCreatePowerPointPending } = useCreatePowerPoint();
  const { mutateAsync: createImages, isPending: isCreateImagesPending } = useCreateImages();

  /* Hooks */
  const { segmentTrackEvent } = useSegment();
  const isNewExportOptionsEnabled = useFeatureFlag("newExportOptions");

  /* Actions */
  const { setSortBy } = useReportFiltersStore((state) => state.reportFiltersActions);
  const setMetric = useReportsSceneStore((state) => state.setMetric);

  /* Stores */
  const { open } = useModalStore((state) => state.modalActions);
  const sortBy = useReportFiltersStore((state) => state.sortBy);
  const metric = useReportsSceneStore((state) => state.metric);

  /* Handlers */
  const handleReportsExport = async () => {
    // track the export event
    segmentTrackEvent("Study Exported", {
      projectId,
      studyId,
      exportType: "Presentation",
      studyType: "scene",
    });
    // get the taskId before opening the modal
    const { taskId } = await createPowerPoint({ projectId, studyId });
    open(<ExportPowerPointModal projectId={projectId} studyId={studyId} taskId={taskId} />);
  };

  const handleVisualsExport = async () => {
    segmentTrackEvent("Study Exported", {
      projectId,
      studyId,
      exportType: "Visuals",
      studyType: "scene",
    });
    const { taskId } = await createImages({ projectId, studyId, type: "scene" });
    open(<ExportVisualsModal projectId={projectId} studyId={studyId} taskId={taskId} />);
  };

  const handleDataExport = () => {
    segmentTrackEvent("Study Exported", {
      projectId,
      studyId,
      exportType: "Data",
      studyType: "scene",
    });
    open(<ExportDataModal projectId={projectId} studyId={studyId} />);
  };

  const handleSortKPI = (value: string) => {
    setSortBy(value as "asc" | "desc");
  };

  const handleEditStudy = () => {
    // Segment tracking
    segmentTrackEvent("Study Editing Initiated", {
      projectId,
      studyId,
      studyType: "scene",
    });

    // This modal will handle the navigation to the study review page
    open(<EditStudyModal projectId={projectId} studyId={studyId} />);
  };

  const handleMetricChange = (newMetric: SceneMetric) => {
    // Segment tracking
    segmentTrackEvent("Scene Report Metric Changed", {
      projectId,
      studyId,
      from: metric,
      to: newMetric,
    });

    setMetric(newMetric);
  };

  // Get display name for metric
  const getDisplayName = (metricType: SceneMetric) => {
    switch (metricType) {
      case "vis":
        return "Visibility";
      case "pop":
        return "Probability of Perception (POP)";
      case "soa":
        return "Share of Attention (SOA)";
      case "pow":
        return "Attention Power Factor (POW)";
      default:
        return metricType;
    }
  };

  const handleInfoModalClick = () => {
    // Segment tracking
    segmentTrackEvent("Study Report Tooltip Viewed", {
      name: metric,
      value: getDisplayName(metric),
      reportType: metric,
      studyId,
      projectId,
      studyType: "scene",
    });
  };

  /* Render */
  return (
    <>
      {/* Primary Toolbar */}
      <Toolbar>
        <ToolbarItem>
          {/* Report Header */}
          <div className="flex items-center gap-2">
            <h1>{getDisplayName(metric)}</h1>
            <InfoModalTrigger modalContent={getSceneInfoModal(metric)} onClick={handleInfoModalClick} config={{ className: "w-full max-w-[591px]" }} />
          </div>
        </ToolbarItem>
        <ToolbarItem>
          <Button onClick={handleEditStudy}>
            <PencilSquareIcon className="size-3.5" />
            Edit study
          </Button>
          {isNewExportOptionsEnabled ? (
            <CustomDropdown
              align="end"
              triggerBtnClassname="p-0"
              triggerBtn={
                <Button>
                  <ArrowUpOnSquareIcon className="size-3.5" />
                  Export
                </Button>
              }
            >
              <CustomDropdownItem onClick={handleReportsExport} disabled={isCreatePowerPointPending}>
                <ComputerDesktopIcon className="size-4" />
                Presentation
              </CustomDropdownItem>
              <CustomDropdownItem onClick={handleVisualsExport} disabled={isCreateImagesPending}>
                <PhotoIcon className="size-4" />
                Visuals
              </CustomDropdownItem>
              <CustomDropdownItem onClick={handleDataExport}>
                <TableCellsIcon className="size-4" />
                Data (.csv)
              </CustomDropdownItem>
            </CustomDropdown>
          ) : (
            <Button onClick={handleReportsExport} disabled={isCreatePowerPointPending}>
              <ArrowUpOnSquareIcon className="size-3.5" />
              Export
            </Button>
          )}
        </ToolbarItem>
      </Toolbar>

      {/* Secondary Toolbar with Metric Filters */}
      {showFilters && (
        <Toolbar>
          <ToolbarItem>
            {/* Metric Buttons */}
            <Button selected={metric === "vis"} onClick={() => handleMetricChange("vis")}>
              Visibility
            </Button>
            <Button selected={metric === "pop"} onClick={() => handleMetricChange("pop")}>
              POP
            </Button>
            <Button selected={metric === "soa"} onClick={() => handleMetricChange("soa")}>
              SOA
            </Button>
            <Button selected={metric === "pow"} onClick={() => handleMetricChange("pow")}>
              POW
            </Button>
          </ToolbarItem>

          {/* Right Content or Sort */}
          <ToolbarItem className="ml-auto">{rightContent}</ToolbarItem>

          {/* Sort */}
          <ToolbarItem>
            <Select onChange={handleSortKPI} defaultValue={sortBy}>
              <SelectItem value="desc">Sort by: Highest score</SelectItem>
              <SelectItem value="asc">Sort by: Lowest score</SelectItem>
            </Select>
          </ToolbarItem>
        </Toolbar>
      )}
    </>
  );
};
